import * as React from 'react';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CastleOutlinedIcon from '@mui/icons-material/CastleOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';

interface LandingPageProps {
  brandMarkUrl?: string;
}

const productScores = [
  {
    label: 'Value Score',
    score: '81.8%',
    meaning: 'Strong fundamentals',
    tone: 'value',
    icon: PointOfSaleOutlinedIcon,
  },
  {
    label: 'Management Score',
    score: '96.2%',
    meaning: 'Spectacular ROI for shareholders',
    tone: 'management',
    icon: EngineeringOutlinedIcon,
  },
  {
    label: 'Moat Score',
    score: '56.7%',
    meaning: 'Average Moat',
    tone: 'moat',
    icon: CastleOutlinedIcon,
  },
  {
    label: 'Debt Score',
    score: '91.0%',
    meaning: 'Excellent debt management',
    tone: 'debt',
    icon: SavingsOutlinedIcon,
  },
];

const financialHistoryPeriods = ['1Y', '3Y', '5Y', '10Y'];

const financialHistoryRows = [
  {
    label: 'Equity growth',
    values: [
      { value: '32.0', tone: 'strong' },
      { value: '16.0', tone: 'strong' },
      { value: '6.0', tone: 'mixed' },
      { value: '-3.0', tone: 'weak' },
    ],
    score: '5.0',
  },
  {
    label: 'Net income growth',
    values: [
      { value: '19.0', tone: 'strong' },
      { value: '4.0', tone: 'mixed' },
      { value: '14.0', tone: 'strong' },
      { value: '8.0', tone: 'mixed' },
    ],
    score: '8.0',
  },
  {
    label: 'Cash flow growth',
    values: [
      { value: '-6.0', tone: 'weak' },
      { value: '-3.0', tone: 'weak' },
      { value: '7.0', tone: 'mixed' },
      { value: '3.0', tone: 'mixed' },
    ],
    score: '3.0',
  },
  {
    label: 'Revenue growth',
    values: [
      { value: '6.0', tone: 'mixed' },
      { value: '2.0', tone: 'mixed' },
      { value: '9.0', tone: 'mixed' },
      { value: '6.0', tone: 'mixed' },
    ],
    score: '6.0',
  },
];

const pillars = [
  {
    title: 'True profitability',
    body: 'Measure what the business actually earns, not how loudly the market talks about it.',
    icon: AssessmentRoundedIcon,
  },
  {
    title: 'Capital efficiency',
    body: 'See how effectively management turns the capital it controls into durable results.',
    icon: AccountBalanceRoundedIcon,
  },
  {
    title: 'A long track record',
    body: 'Follow consistent growth over years so one exceptional quarter never tells the whole story.',
    icon: TimelineRoundedIcon,
  },
];

const workflowSteps = [
  {
    title: 'Search any company',
    body: 'Look up any stock and instantly see its Value Score, broken down into the fundamentals that built it.',
    icon: SearchRoundedIcon,
  },
  {
    title: 'See the full picture, simplified',
    body: 'No spreadsheets required. We translate a decade of financial filings into a clear breakdown of profitability, debt, and growth — the kind of research that used to take a weekend.',
    icon: AssessmentRoundedIcon,
  },
  {
    title: 'Ask Lima, your AI research analyst',
    body: 'Chat with Lima to dig deeper — ask about risks, competitive position, or what changed in the last earnings call.',
    icon: ChatBubbleOutlineRoundedIcon,
  },
  {
    title: 'Or send in your AI research team',
    body: 'For a full workup, hand a company (or a whole watchlist) to your AI Agents. They read years of financial filings, scan earnings calls, build a valuation with margin of safety, and hand you a buy/sell/hold recommendation with full reasoning — while you do something else.',
    icon: GroupsRoundedIcon,
  },
];

const comparisonRows = [
  {
    label: 'Signal',
    typical: 'Trending tickers, sentiment',
    generic: 'General knowledge',
    decode: 'Years of financials, scored',
  },
  {
    label: 'Method',
    typical: 'Inconsistent / editorial',
    generic: 'Not investing-specific',
    decode: 'Rules-based, same for every company',
  },
  {
    label: 'Depth',
    typical: 'Headlines',
    generic: 'Surface-level',
    decode: 'Full filings, earnings calls, valuation',
  },
];

const leaderboardPreviewRows = [
  {
    ticker: 'CPRT',
    company: 'Copart',
    valueScore: '99.9',
    consideration: 'Buy',
    decision: 'Undervalued',
    marginOfSafety: '$49.85',
    tone: 'buy',
  },
  {
    ticker: 'ROL',
    company: 'Rollins',
    valueScore: '99.3',
    consideration: 'Hold',
    decision: 'Expensive',
    marginOfSafety: '$12.58',
    tone: 'hold',
  },
  {
    ticker: 'ISRG',
    company: 'Intuitive Surgical',
    valueScore: '99.2',
    consideration: 'Hold',
    decision: 'Expensive',
    marginOfSafety: '$145.95',
    tone: 'hold',
  },
  {
    ticker: 'TSM',
    company: 'Taiwan Semiconductor',
    valueScore: '99.2',
    consideration: 'Hold',
    decision: 'Expensive',
    marginOfSafety: '$182.07',
    tone: 'hold',
  },
];

function ArrowIcon() {
  return <ArrowForwardRoundedIcon aria-hidden="true" fontSize="small" />;
}

export default function LandingPage({ brandMarkUrl = '/assets/decode-brand-mark.svg' }: LandingPageProps) {
  React.useEffect(() => {
    const scrollToHashSection = () => {
      if (window.location.hash !== '#how-it-works') return;

      window.requestAnimationFrame(() => {
        document.getElementById('how-it-works')?.scrollIntoView({ block: 'start' });
      });
    };

    scrollToHashSection();
    window.addEventListener('hashchange', scrollToHashSection);
    return () => window.removeEventListener('hashchange', scrollToHashSection);
  }, []);

  const handleHowItWorksClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const section = document.getElementById('how-it-works');
    if (!section) return;

    event.preventDefault();
    if (window.location.hash !== '#how-it-works') {
      window.history.pushState(null, '', '/#how-it-works');
    }
    section.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  return (
    <div className="di-landing">
      <header className="di-landing__header">
        <div className="di-landing__shell di-landing__nav-shell">
          <a className="di-landing__brand" href="/" aria-label="Decode Investing home">
            <img src={brandMarkUrl} alt="" />
            <span>Decode Investing</span>
          </a>
          <nav className="di-landing__nav" aria-label="Primary navigation">
            <a className="di-landing__nav-link" href="/signin">Sign in</a>
            <a className="di-landing__button di-landing__button--small" href="/signup">Sign up free</a>
          </nav>
        </div>
      </header>

      <main>
        <section className="di-landing__hero" aria-labelledby="landing-hero-title">
          <div className="di-landing__shell di-landing__hero-grid">
            <div className="di-landing__hero-copy">
              <div className="di-landing__eyebrow">
                <AutoAwesomeRoundedIcon aria-hidden="true" />
                Disciplined research for long-term investors
              </div>
              <h1 id="landing-hero-title">The painkiller for your portfolio.</h1>
              <p className="di-landing__lead">
                If you invest in individual stocks, Decode Investing is a no-brainer. We measure the true profitability of a company — not its hype, not its headlines — and use its historical growth to estimate what it&apos;s actually worth. Cut through the noise. Invest with the numbers.
              </p>
              <div className="di-landing__hero-actions">
                <div>
                  <a className="di-landing__button" href="/signup">
                    Sign up free <ArrowIcon />
                  </a>
                  <span className="di-landing__button-note">No credit card required</span>
                </div>
                <a className="di-landing__text-link" href="/#how-it-works" onClick={handleHowItWorksClick}>
                  See how it works <ArrowIcon />
                </a>
              </div>
              <div className="di-landing__trust-row" aria-label="Product highlights">
                <span><CheckCircleRoundedIcon aria-hidden="true" /> Free forever tier</span>
                <span><CheckCircleRoundedIcon aria-hidden="true" /> Research any company</span>
              </div>
            </div>

            <aside className="di-landing__product-card" aria-label="Illustrative Decode Investing company scores">
              <div className="di-landing__product-glow" aria-hidden="true" />
              <div className="di-landing__product-card-top">
                <span className="di-landing__preview-label">Illustrative example</span>
                <span className="di-landing__preview-context">Product preview</span>
              </div>
              <div className="di-landing__company-row">
                <div className="di-landing__company-mark">EC</div>
                <div>
                  <strong>Example Company</strong>
                  <span>EXM · Business overview</span>
                </div>
              </div>
              <div className="di-landing__product-scores">
                {productScores.map(({ label, score, meaning, tone, icon: Icon }) => (
                  <article className={`di-landing__product-score di-landing__product-score--${tone}`} key={label}>
                    <span className="di-landing__product-score-icon"><Icon aria-hidden="true" /></span>
                    <div>
                      <span className="di-landing__product-score-label">{label}</span>
                      <strong>{score}</strong>
                      <small>{meaning}</small>
                    </div>
                  </article>
                ))}
              </div>
              <div className="di-landing__product-footer">
                <span>10+ years of financial history</span>
                <span>Four scores. One clear picture.</span>
              </div>
            </aside>
          </div>
        </section>

        <section className="di-landing__section di-landing__problem" aria-labelledby="problem-title">
          <div className="di-landing__shell di-landing__narrow">
            <p className="di-landing__kicker">For investors who want more than an index fund</p>
            <h2 id="problem-title">You know stocks aren&apos;t just tickers. So why does research still feel like guesswork?</h2>
            <div className="di-landing__prose di-landing__prose--columns">
              <p>You&apos;ve read the books. You know the names — Buffett, Munger, Phil Town. You understand that a great investment is a great <em>business</em> first. But turning that into a repeatable process means digging through years of financial filings, tracking profitability trends by hand, and trying to tell real competitive advantage apart from a hot news cycle.</p>
              <p>Most tools don&apos;t help. They give you a stock price, headlines and financial tables, not an answer.</p>
            </div>
          </div>
        </section>

        <section className="di-landing__section di-landing__section--tinted" aria-labelledby="profitability-title">
          <div className="di-landing__shell">
            <div className="di-landing__section-heading di-landing__section-heading--center">
              <p className="di-landing__kicker">True profitability, not hype</p>
              <h2 id="profitability-title">We cut through the hype.</h2>
              <p>Popularity is not a signal we track. A trending ticker or a viral headline tells you nothing about whether a business is actually worth owning. We measure the metrics that matter most — real profitability, and how effectively and efficiently a company allocates its capital, sustained over years, not one good quarter. One year&apos;s result never tells the full story, so we look for a long track record of solid growth before a company earns a place on your radar.</p>
              <p>This is the same discipline used by some of the most successful investors in the world — systems Warren Buffett and Charlie Munger would recognize.</p>
            </div>
            <div className="di-landing__pillars">
              {pillars.map(({ title, body, icon: Icon }) => (
                <article className="di-landing__pillar" key={title}>
                  <span className="di-landing__icon-box"><Icon aria-hidden="true" /></span>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="di-landing__section" aria-labelledby="informed-title">
          <div className="di-landing__shell di-landing__split">
            <div>
              <p className="di-landing__kicker">Be informed</p>
              <h2 id="informed-title">Know when to buy, when to sell, and when to just hold.</h2>
            </div>
            <div className="di-landing__feature-copy">
              <p>Decode Investing isn&apos;t only about a score — it&apos;s about understanding. We give you the research tools to build a real, working knowledge of a company, not just a number to react to. That means knowing not just <em>whether</em> a business is a good one, but <em>when</em> the price makes it a good time to act.</p>
              <div className="di-landing__decision-row" aria-label="Investment considerations">
                <span>Buy</span><span>Sell</span><span>Hold</span>
              </div>
            </div>
          </div>
        </section>

        <section className="di-landing__section di-landing__section--dark" aria-labelledby="lima-title">
          <div className="di-landing__shell di-landing__split di-landing__split--reverse-mobile">
            <div className="di-landing__assistant-card" aria-hidden="true">
              <div className="di-landing__assistant-header">
                <span className="di-landing__assistant-panel-icon"><i /><i /></span>
                <img className="di-landing__assistant-avatar" src="/images/lima_avatar.jpg" alt="" />
                <div>
                  <strong>Lima</strong>
                  <small>AI Stock Market Research Analyst — ask me anything about stocks, earnings, filings, or screening.</small>
                </div>
              </div>
              <div className="di-landing__assistant-conversation">
                <div className="di-landing__assistant-prompt">What is driving Costco&apos;s long-term profitability?</div>
                <div className="di-landing__assistant-answer">
                  <strong className="di-landing__assistant-answer-title">Costco&apos;s profitability drivers</strong>
                  <span className="di-landing__assistant-intro">Costco&apos;s model compounds through three durable drivers:</span>
                  <div className="di-landing__assistant-finding">
                    <strong>1. Membership economics</strong>
                    <span>High renewal rates create recurring revenue and keep Costco focused on member value.</span>
                  </div>
                  <div className="di-landing__assistant-finding">
                    <strong>2. Scale and efficiency</strong>
                    <span>Fast inventory turns and purchasing power support low prices and steady cash flow.</span>
                  </div>
                  <div className="di-landing__assistant-bottom-line">
                    <strong>Bottom line:</strong> The moat depends on member loyalty, disciplined pricing, and efficient operations.
                  </div>
                  <div className="di-landing__assistant-disclaimer">My research is only a starting point — please verify the data and conduct your own due diligence.</div>
                  <div className="di-landing__assistant-actions">
                    <ContentCopyOutlinedIcon />
                    <FileDownloadOutlinedIcon />
                    <ShareOutlinedIcon />
                    <ShowChartRoundedIcon />
                  </div>
                </div>
              </div>
              <div className="di-landing__assistant-composer">
                <LightbulbOutlinedIcon />
                <span>Try: Compare Costco with its top competitors</span>
                <b><SendRoundedIcon /></b>
              </div>
            </div>
            <div>
              <p className="di-landing__kicker">Meet Lima, your AI investment analyst</p>
              <h2 id="lima-title">Not your average chatbot.</h2>
              <p> Lima is built and tuned specifically on the principles and methodology of the world&apos;s best value investors — not general internet knowledge dressed up as financial advice. Ask Lima about any company and get the reasoning of a seasoned analyst: what&apos;s driving the numbers, what the real risks are, and whether the story holds up under scrutiny.</p>
              <p className="di-landing__supporting-line">Free to use, with generous usage included — no expensive subscription required to get started.</p>
            </div>
          </div>
        </section>

        <section className="di-landing__section" aria-labelledby="team-title">
          <div className="di-landing__shell di-landing__split">
            <div>
              <p className="di-landing__kicker">Your AI research team</p>
              <h2 id="team-title">Months of research, done for you.</h2>
              <p>Real due diligence means reading years of financial filings and sitting through hours of earnings calls — work that takes most investors weeks they don&apos;t have. Your AI Agents do it for you. They review multiple years of a company&apos;s filings and communications, cross-check the details, and bring you a clear consensus — so you get straight to the point on whether a company is actually worth a place in your portfolio.</p>
              <p className="di-landing__microcopy">Most analyses complete in a few hours. You stay in control the whole time — jump in and redirect the research whenever you want.</p>
              <a className="di-landing__text-link" href="https://agents.decodeinvesting.com/" target="_blank" rel="noopener noreferrer">
                Meet your AI research team <ArrowIcon />
              </a>
            </div>
            <div
              className="di-landing__team-visual"
              role="img"
              aria-label="A request to Lima becomes a research project the investor can follow and direct, ending with a completed recommendation."
            >
              <div className="di-landing__team-lima">
                <img src="/images/lima_avatar.jpg" alt="" />
                <div>
                  <strong>Lima</strong>
                  <small>AI Stock Market Research Analyst</small>
                </div>
                <span><i /> Online</span>
              </div>

              <div className="di-landing__team-request">
                Create a project to determine whether I should buy AAPL.
              </div>

              <div className="di-landing__team-response">
                <p>I&apos;ve created a dedicated project to determine whether Apple is a good investment. The research will cover its financial health, valuation, competitive advantage, management, risks, and growth prospects.</p>
                <p className="di-landing__team-project-track">
                  You can track the progress of this project here:{' '}
                  <strong>Track &quot;Should You Buy AAPL?&quot; <ArrowIcon /></strong>
                </p>
                <p className="di-landing__team-project-note">From the project page, review tasks and reports or redirect the research whenever you need to.</p>
              </div>

              <div className="di-landing__team-connector" aria-hidden="true"><i /></div>

              <div className="di-landing__team-notification">
                <div className="di-landing__team-notification-heading">
                  <strong>Notification</strong>
                  <span>NEW</span>
                </div>
                <div className="di-landing__team-notification-card">
                  <small>RESEARCH COMPLETE</small>
                  <strong>AAPL Investment Verdict Ready</strong>
                  <p>We&apos;ve aggregated the research into a detailed final analysis with a clear recommendation.</p>
                  <span>VIEW PROJECT <ArrowIcon /></span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="di-landing__section di-landing__section--tinted di-landing__workflow" aria-labelledby="workflow-title">
          <div className="di-landing__shell">
            <div className="di-landing__section-heading">
              <p className="di-landing__kicker">How it works</p>
              <h2 id="workflow-title">From &quot;I wonder about this stock&quot; to a clear answer — in minutes, not weeks.</h2>
            </div>
            <ol className="di-landing__steps">
              {workflowSteps.map(({ title, body, icon: Icon }, index) => (
                <li key={title}>
                  <div className="di-landing__step-marker"><span>{index + 1}</span><Icon aria-hidden="true" /></div>
                  <div><h3>{title}</h3><p>{body}</p></div>
                </li>
              ))}
            </ol>
            <div className="di-landing__center-action">
              <a className="di-landing__button" href="/signup">Start your first analysis, free <ArrowIcon /></a>
            </div>
          </div>
        </section>

        <section className="di-landing__section" aria-labelledby="history-title">
          <div className="di-landing__shell di-landing__split">
            <div className="di-landing__history-visual" aria-label="Illustrative Decode Investing financial health breakdown">
              <div className="di-landing__history-header">
                <div>
                  <strong>Historical Growth Rates</strong>
                  <small>Illustrative company · figures shown as %</small>
                </div>
                <span>10-year view</span>
              </div>
              <table className="di-landing__history-table">
                <caption className="di-landing__sr-only">Illustrative historical company growth rates and competitive advantage score</caption>
                <thead>
                  <tr>
                    <th scope="col">Growth rate</th>
                    {financialHistoryPeriods.map((period) => <th scope="col" key={period}>{period}</th>)}
                    <th scope="col">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {financialHistoryRows.map(({ label, values, score }) => (
                    <tr key={label}>
                      <th scope="row">{label}</th>
                      {values.map(({ value, tone }, index) => (
                        <td className={`di-landing__history-cell--${tone}`} key={financialHistoryPeriods[index]}>{value}</td>
                      ))}
                      <td className="di-landing__history-score">{score}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">Competitive advantage</th>
                    <td colSpan={4}>Long-term growth consistency</td>
                    <td><strong>56.7</strong></td>
                  </tr>
                </tfoot>
              </table>
              <div className="di-landing__health-signals">
                <div><span><i />Capital efficiency</span><strong>28.0% ROIC</strong></div>
                <div><span><i />Debt discipline</span><strong>1.6 year payoff</strong></div>
                <div><span><i />Cash generation</span><strong>Positive 10Y FCF</strong></div>
              </div>
            </div>
            <div>
              <p className="di-landing__kicker">Simplified financial health</p>
              <h2 id="history-title">Years of financial history, distilled into a picture you can actually read.</h2>
              <p>You don&apos;t need to be a CFA to understand a company&apos;s financial health — you just need the right lens. We turn dense financial filings into a clear breakdown of what matters: how efficiently a company uses capital, how it manages debt, and how consistently it&apos;s grown over one, three, five, and ten years. See the trend, not just a snapshot.</p>
            </div>
          </div>
        </section>

        <section className="di-landing__section di-landing__leaderboard" aria-labelledby="leaderboard-title">
          <div className="di-landing__shell di-landing__split">
            <div>
              <p className="di-landing__kicker">The leaderboard</p>
              <h2 id="leaderboard-title">See which companies actually earn their valuation — updated daily.</h2>
              <p>Every company we cover is scored on the same fundamentals — profitability, debt discipline, and consistent long-term growth — and ranked accordingly. The leaderboard updates as new financial results come in, so you&apos;re always looking at current data, not last quarter&apos;s story.</p>
              <a className="di-landing__text-link" href="/leaderboard">Browse the leaderboard <ArrowIcon /></a>
            </div>
            <div className="di-landing__ranking-card">
              <div className="di-landing__ranking-meta">
                <strong>Top-ranked companies</strong>
                <span>Illustrative preview · Updated daily</span>
              </div>
              <table className="di-landing__ranking-table">
                <caption className="di-landing__sr-only">Illustrative leaderboard with company scores and valuation signals</caption>
                <thead>
                  <tr className="di-landing__ranking-head">
                    <th scope="col">Company</th><th scope="col">Value score</th><th scope="col">Consideration</th><th scope="col">Decision</th><th scope="col">Margin of safety</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboardPreviewRows.map(({ ticker, company, valueScore, consideration, decision, marginOfSafety, tone }) => (
                    <tr className={`di-landing__ranking-row di-landing__ranking-row--${tone}`} key={ticker}>
                      <th className="di-landing__ranking-company" scope="row">
                        <b>{ticker}</b>
                        <strong>{company}</strong>
                      </th>
                      <td className="di-landing__ranking-score" data-label="Value score">{valueScore}</td>
                      <td className={`di-landing__ranking-consideration di-landing__ranking-consideration--${tone}`} data-label="Consideration">{consideration}</td>
                      <td className={`di-landing__ranking-decision di-landing__ranking-decision--${decision.toLowerCase()}`} data-label="Decision">{decision}</td>
                      <td className="di-landing__ranking-margin" data-label="Margin of safety">{marginOfSafety}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="di-landing__section" aria-labelledby="different-title">
          <div className="di-landing__shell">
            <div className="di-landing__section-heading di-landing__section-heading--center">
              <p className="di-landing__kicker">What makes us different</p>
              <h2 id="different-title">We&apos;re not another stock-tip site. We&apos;re not a general chatbot with a finance skin.</h2>
              <p>Most financial sites chase clicks — trending tickers, momentum plays, whatever&apos;s popular this week. We only look at what a business has actually done. And our AI isn&apos;t a repurposed general-purpose assistant — it&apos;s decoded from the analysis methodology of the world&apos;s best value investors and built specifically to reason the way a disciplined fundamental investor would.</p>
            </div>

            <div className="di-landing__comparison-table-wrap">
              <table className="di-landing__comparison-table">
                <caption className="di-landing__sr-only">Comparison of typical finance sites, generic AI chatbots, and Decode Investing</caption>
                <thead><tr><th scope="col">Approach</th><th scope="col">Typical finance sites</th><th scope="col">Generic AI chatbots</th><th scope="col">Decode Investing</th></tr></thead>
                <tbody>
                  {comparisonRows.map((row) => (
                    <tr key={row.label}><th scope="row">{row.label}</th><td>{row.typical}</td><td>{row.generic}</td><td><CheckCircleRoundedIcon aria-hidden="true" />{row.decode}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="di-landing__comparison-cards">
              {comparisonRows.map((row) => (
                <article key={row.label}>
                  <h3>{row.label}</h3>
                  <dl>
                    <div><dt>Typical finance sites</dt><dd>{row.typical}</dd></div>
                    <div><dt>Generic AI chatbots</dt><dd>{row.generic}</dd></div>
                    <div className="decode"><dt>Decode Investing</dt><dd><CheckCircleRoundedIcon aria-hidden="true" />{row.decode}</dd></div>
                  </dl>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="di-landing__section di-landing__section--tinted" aria-labelledby="audience-title">
          <div className="di-landing__shell">
            <div className="di-landing__section-heading di-landing__section-heading--center">
              <p className="di-landing__kicker">Who it&apos;s for</p>
              <h2 id="audience-title">Built for investors who want to go beyond the index fund</h2>
            </div>
            <div className="di-landing__audience-grid">
              <article><span>01</span><TrendingUpRoundedIcon aria-hidden="true" /><p>Maybe you&apos;ve got money in index funds and you&apos;re curious about picking individual stocks — you&apos;ve heard the names Buffett and Munger, but you&apos;re not sure how to actually apply what they do. This is where you start.</p></article>
              <article><span>02</span><AccountBalanceRoundedIcon aria-hidden="true" /><p>Maybe you&apos;ve read <em>Rule #1</em> or <em>Payback Time</em> and you know the framework — moat, management, meaning, margin of safety — but applying it to real companies, in real time, is slow. This is the tool that closes that gap.</p></article>
            </div>
          </div>
        </section>

        <section className="di-landing__final-cta" aria-labelledby="final-cta-title">
          <div className="di-landing__shell">
            <div className="di-landing__final-card">
              <div className="di-landing__final-orb" aria-hidden="true" />
              <p className="di-landing__kicker">Start with the numbers</p>
              <h2 id="final-cta-title">Stop researching stocks the hard way.</h2>
              <p>Create your free account and see your first company&apos;s Value Score in the next two minutes.</p>
              <a className="di-landing__button di-landing__button--light" href="/signup">Sign up free <ArrowIcon /></a>
              <small>Free forever tier. No credit card. Upgrade only if you want more.</small>
            </div>
          </div>
        </section>
      </main>

      <footer className="di-landing__footer">
        <div className="di-landing__shell">
          <div className="di-landing__footer-top">
            <a className="di-landing__brand" href="/" aria-label="Decode Investing home"><img src={brandMarkUrl} alt="" /><span>Decode Investing</span></a>
            <nav aria-label="Footer navigation"><a href="/blog">Blog</a><a href="/faq">FAQ</a><a href="/pricing">Pricing</a><a href="/privacy">Privacy</a><a href="/tos">Terms</a></nav>
          </div>
          <p className="di-landing__disclaimer">Decode Investing provides research and information for educational purposes only and does not provide financial advice. Past performance does not guarantee future results.</p>
          <p className="di-landing__copyright">© {new Date().getFullYear()} Decode Investing. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
